import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import PatientSlotAdditionalInfo, { AdditionalInfoWrapper } from './PatientSlotAdditionalInfo';
import PatientSlotInfo, { Container as PatientSlotInfoContainer } from './PatientSlotInfo';
import DischargeAction from '../../entities/patient/views/DischargeActionLiason';
import MedsDeliveredAction from '../../entities/patient/views/MedsDeliveredAction';
import {
  formatPatientCardScheduledOr,
  getPatientCardProcedureTimeParts,
  PATIENT_CARD_SCHEDULED_OR_COLUMN_WIDTH,
} from './patientCardConfig';

const startTimeFontSize = props =>
  props.$expandedOrTileView ? '2.25rem' : props.size === 'large' ? '2.25em' : '1.5em';
const startTimeFontSizeUnder900 = props =>
  props.$expandedOrTileView ? '1.875rem' : props.size === 'large' ? '2.25em' : '1rem';
const startTimeFontSizeUnder600 = props =>
  props.$expandedOrTileView ? '1.5rem' : props.size === 'large' ? '2.25em' : '2vh';

const ScheduledStartTime = styled.div`
  grid-column: 1;
  grid-row: 1;
  align-self: baseline;
  white-space: nowrap;
  font-size: ${startTimeFontSize};
  line-height: ${props => (props.$expandedOrTileView ? '1.15' : '1.4')};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${props => props.theme.secondaryTextColor.string()};

  @media (max-height: 900px) {
    font-size: ${startTimeFontSizeUnder900};
  }

  @media (max-height: 600px) {
    font-size: ${startTimeFontSizeUnder600};
    line-height: ${props => (props.$expandedOrTileView || props.size === 'large' ? '1' : '1.4')};
  }
`;

const ScheduledFinishTime = styled.div`
  grid-column: 1;
  grid-row: ${props => (props.$primaryRow ? 1 : 2)};
  align-self: baseline;
  margin-top: ${props => (props.$primaryRow ? 0 : '0.25rem')};
  white-space: nowrap;
  font-size: ${props => (props.$expandedOrTileView ? '1.625rem' : props.size === 'large' ? '1.75rem' : '1.125rem')};
  line-height: ${props => (props.$expandedOrTileView ? '1.2' : '1.1')};
  font-variant-numeric: tabular-nums;
  color: ${props => props.theme.secondaryTextColor.string()};
  opacity: 0.5;

  @media (max-height: 900px) {
    font-size: ${props => (props.$expandedOrTileView ? '1.375rem' : props.size === 'large' ? '1.75rem' : '0.875rem')};
  }

  @media (max-height: 600px) {
    font-size: ${props => (props.$expandedOrTileView ? '1.125rem' : props.size === 'large' ? '1.75rem' : '2vh')};
  }
`;

const ProcedureStartTimeSpacer = styled(ScheduledStartTime).attrs({ 'aria-hidden': true })`
  width: 5ch;
  visibility: hidden;
`;

const ProcedureFinishTimeSpacer = styled(ScheduledFinishTime).attrs({ 'aria-hidden': true })`
  width: 5ch;
  visibility: hidden;
`;

const ScheduledRoomName = styled.div`
  grid-column: 1;
  grid-row: ${props => (props.$showProcedureTimes ? 3 : 2)};
  align-self: baseline;
  width: ${PATIENT_CARD_SCHEDULED_OR_COLUMN_WIDTH};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: ${props => (props.$expandedOrTileView ? '1.625rem' : props.size === 'large' ? '1.75rem' : '1.125rem')};
  line-height: ${props => (props.$expandedOrTileView ? '1.2' : '1.1')};
  color: ${props => props.theme.textColor.string()};

  @media (max-height: 900px) {
    font-size: ${props => (props.$expandedOrTileView ? '1.375rem' : props.size === 'large' ? '1.75rem' : '0.875rem')};
  }

  @media (max-height: 600px) {
    font-size: ${props => (props.$expandedOrTileView ? '1.125rem' : props.size === 'large' ? '1.75rem' : '2vh')};
  }
`;

const PatientInfoWrapper = styled.div`
  flex: 1;
  overflow: hidden;

  ${props =>
    props.$useScheduledLayout &&
    css`
      display: grid;
      grid-template-columns: max-content minmax(0, 1fr);
      grid-template-rows: ${props =>
        props.$showScheduledOr && props.$showProcedureTimes ? 'auto auto auto' : 'auto auto'};
      align-items: baseline;
      column-gap: 0.5rem;

      > :not(${ScheduledStartTime}):not(${ScheduledFinishTime}):not(${ProcedureStartTimeSpacer}):not(
          ${ProcedureFinishTimeSpacer}
        ):not(${ScheduledRoomName}) {
        grid-column: 2;
        min-width: 0;
      }

      > ${PatientSlotInfoContainer} {
        grid-row: 1;
      }

      > ${AdditionalInfoWrapper} {
        grid-row: ${props => (props.$showScheduledOr ? (props.$showProcedureTimes ? 3 : 2) : 2)};
      }
    `}

  ${props =>
    props.miniView &&
    css`
      min-height: 3.8em;
    `}
`;

const DischargeActionContainer = styled.div`
  display: flex;
  white-space: nowrap;
  flex: 0 1 0;
  justify-content: flex-end;
  min-width: auto;
  gap: 0.5em;
`;

const PatientInfo = ({
  status,
  physician,
  procedure,
  patient,
  patientId,
  patientType,
  patientData,
  bed,
  showBedNumber = false,
  timeEntered,
  compactView,
  readyForOr,
  readyForSurgeon,
  readyInOr,
  roomName,
  familyReady,
  readyForPickup,
  isHoldProcedure,
  isBlockNerve,
  expectedExitTime,
  size,
  allowWrap,
  miniView,
  preOpFocused,
  pacuFocused,
  showPreOpNote,
  preOpNote,
  showPacuNote,
  pacuNote,
  showPatientNote,
  patientNote,
  isDPU,
  showRoom,
  showDuration,
  blockNerveFollow,
  called,
  transferredToChair,
  transferredToPT,
  ptComplete,
  isPreOp,
  isPacu,
  isSuperAdmin,
  noAdditionalInfo,
  messagingView,
  grow,
  readyForAnesthesia,
  isSpinalTimeout,
  showProcedureType = true,
  room,
  showLatePreOpIndicator,
  showPacuLateIndicator,
  isGroupAdmin,
  expandedOrTileView,
  screenType,
  showPatientAgeAndSex,
  showPatientProcedureTimes,
  showPatientScheduledOr,
}) => {
  const { startTime, endTime } = showPatientProcedureTimes
    ? getPatientCardProcedureTimeParts(patientData)
    : { startTime: null, endTime: null };
  const scheduledOr = showPatientScheduledOr ? formatPatientCardScheduledOr(patientData) : '';
  const useScheduledLayout = Boolean(showPatientProcedureTimes || showPatientScheduledOr);

  return (
    <PatientInfoWrapper
      miniView={miniView}
      $useScheduledLayout={useScheduledLayout}
      $showProcedureTimes={Boolean(showPatientProcedureTimes)}
      $showScheduledOr={Boolean(showPatientScheduledOr)}
    >
      {showPatientProcedureTimes && (
        <>
          <ProcedureStartTimeSpacer size={size} $expandedOrTileView={expandedOrTileView}>
            00:00
          </ProcedureStartTimeSpacer>
          <ProcedureFinishTimeSpacer size={size} $expandedOrTileView={expandedOrTileView} $primaryRow={false}>
            00:00
          </ProcedureFinishTimeSpacer>
        </>
      )}
      {startTime && (
        <ScheduledStartTime size={size} $expandedOrTileView={expandedOrTileView}>
          {startTime}
        </ScheduledStartTime>
      )}
      {endTime && (
        <ScheduledFinishTime size={size} $expandedOrTileView={expandedOrTileView} $primaryRow={!startTime}>
          {endTime}
        </ScheduledFinishTime>
      )}
      {showPatientScheduledOr && (
        <ScheduledRoomName
          size={size}
          $expandedOrTileView={expandedOrTileView}
          $showProcedureTimes={Boolean(showPatientProcedureTimes)}
          title={scheduledOr || undefined}
        >
          {scheduledOr}
        </ScheduledRoomName>
      )}
      <PatientSlotInfo
        bed={bed}
        showBedNumber={showBedNumber}
        physician={physician}
        patient={patient}
        patientId={patientId}
        patientType={patientType}
        procedure={procedure}
        size={size}
        readyForSurgeon={readyForSurgeon}
        readyForOr={readyForOr}
        readyInOr={readyInOr}
        familyReady={familyReady}
        readyForPickup={readyForPickup}
        allowWrap={allowWrap}
        miniView={miniView}
        isHoldProcedure={isHoldProcedure}
        isBlockNerve={isBlockNerve}
        called={called}
        transferredToChair={transferredToChair}
        transferredToPT={transferredToPT}
        ptComplete={ptComplete}
        isDPU={isDPU}
        isPreOp={isPreOp}
        isPacu={isPacu}
        isSuperAdmin={isSuperAdmin}
        messagingView={messagingView}
        patientData={patientData}
        grow={grow}
        readyForAnesthesia={readyForAnesthesia}
        isSpinalTimeout={isSpinalTimeout}
        timeEntered={timeEntered}
        room={room}
        showLatePreOpIndicator={showLatePreOpIndicator}
        showPacuLateIndicator={showPacuLateIndicator}
        isGroupAdmin={isGroupAdmin}
        medsDeliveryStatus={patientData?.medsDeliveryStatus}
        expandedOrTileView={expandedOrTileView}
        screenType={screenType}
        showPatientAgeAndSex={showPatientAgeAndSex}
        compactProcedureTimeLayout={useScheduledLayout}
      />
      {!noAdditionalInfo && !messagingView && (
        <PatientSlotAdditionalInfo
          bed={bed}
          procedure={procedure}
          timeEntered={timeEntered}
          compactView={compactView}
          status={status}
          size={size}
          miniView={miniView}
          roomName={roomName}
          expectedExitTime={expectedExitTime}
          preOpFocused={preOpFocused}
          pacuFocused={pacuFocused}
          showPreOpNote={showPreOpNote}
          showPacuNote={showPacuNote}
          showPatientNote={showPatientNote}
          preOpNote={preOpNote}
          pacuNote={pacuNote}
          patientNote={patientNote}
          showRoom={showRoom}
          showDuration={showDuration}
          blockNerveFollow={blockNerveFollow}
          showProcedureType={showProcedureType}
          expandedOrTileView={expandedOrTileView}
        />
      )}
      {messagingView && !!patient && (
        <DischargeActionContainer>
          <MedsDeliveredAction id={patientData?.id} patient={patientData} />
          <DischargeAction id={patientData?.id} patient={patientData} />
        </DischargeActionContainer>
      )}
    </PatientInfoWrapper>
  );
};

PatientInfo.propTypes = {
  status: PropTypes.oneOf(['alert', 'occupied', 'empty', 'ready', 'cleaning']),
  physician: PropTypes.string,
  procedure: PropTypes.string,
  patient: PropTypes.any,
  timeEntered: PropTypes.instanceOf(Date),
  compactView: PropTypes.bool,
  ready: PropTypes.bool,
  bed: PropTypes.string,
};

export default PatientInfo;
